<script setup lang="ts">
import { computed } from 'vue';

import type { FmeaNode } from '@/types/domain';

const props = defineProps<{
  node: FmeaNode;
  expanded: Set<number>;
  showUnrated: boolean;
  activeFunctionId?: number | null;
}>();
const emit = defineEmits<{
  toggle: [id: number];
  pick: [node: FmeaNode];
  rate: [node: FmeaNode];
  add: [node: FmeaNode];
  remove: [node: FmeaNode];
}>();

/** 每种条目下能加什么子级，和后端 PARENT_RULES 一致 */
const CHILD_LABEL: Record<string, string> = {
  process_step: '加功能',
  function: '加失效',
  failure: '加措施',
};

const KIND_LABEL: Record<string, string> = {
  process_step: '过程步骤',
  function: '功能',
  failure: '失效',
  action: '措施',
  work_element: '工作要素',
  characteristic: '特殊特性',
  unknown: '条目',
};
/** AIAG-VDA 失效链 */
const FAILURE_LABEL: Record<string, string> = {
  effect: '影响 FE',
  mode: '模式 FM',
  cause: '原因 FC',
};

function hasUnrated(node: FmeaNode): boolean {
  // 只有失效模式才需要评分；影响与原因行本就无 S/O/D
  if (node.failureType === 'mode' && node.severity === null) return true;
  return node.children.some(hasUnrated);
}

const children = computed(() =>
  (props.showUnrated ? props.node.children.filter(hasUnrated) : props.node.children));
const open = computed(() => props.expanded.has(props.node.id));
const isUnrated = computed(() =>
  props.node.failureType === 'mode' && props.node.severity === null);
const isAiTarget = computed(() =>
  props.node.kind === 'function' && props.activeFunctionId === props.node.id);
const canAddChild = computed(() => Boolean(CHILD_LABEL[props.node.kind]));
const AP_LABEL: Record<string, string> = { H: '高', M: '中', L: '低' };
const apLabel = computed(() => AP_LABEL[props.node.actionPriority ?? ''] ?? '');
const sod = computed(() => (props.node.severity === null
  ? '未评价'
  : `S${props.node.severity} / O${props.node.occurrence ?? '-'} / D${props.node.detection ?? '-'}`));
</script>

<template>
  <div class="fmea-branch">
    <div class="fmea-row" :class="[`kind-${node.kind}`, { unrated: isUnrated, 'ai-target': isAiTarget }]">
      <button
        v-if="children.length"
        class="fmea-toggle"
        type="button"
        :aria-expanded="open"
        @click="emit('toggle', node.id)"
      >{{ open ? '−' : '+' }}</button>
      <span v-else class="fmea-toggle placeholder">·</span>

      <i class="fmea-kind">{{ KIND_LABEL[node.kind] || node.kind }}</i>
      <i v-if="node.failureType" class="fmea-ft" :class="`ft-${node.failureType}`">
        {{ FAILURE_LABEL[node.failureType] || node.failureType }}
      </i>
      <span class="fmea-title" :title="node.title">{{ node.title }}</span>
      <em v-if="node.failureType === 'mode'" class="fmea-sod" :class="{ missing: isUnrated }">{{ sod }}</em>
      <b
        v-if="node.actionPriority"
        class="fmea-ap"
        :class="`ap-${node.actionPriority.toLowerCase()}`"
        :title="`AIAG-VDA 措施优先级 ${node.actionPriority}（RPN 参考 ${node.rpn}）`"
      >AP {{ node.actionPriority }} {{ apLabel }}</b>
      <small v-if="children.length" class="fmea-count">{{ children.length }}</small>
      <span class="fmea-ops">
        <button
          v-if="node.kind === 'function'"
          type="button"
          :title="isAiTarget ? '取消选择' : '选为 AI 生成目标'"
          :class="{ active: isAiTarget }"
          @click="emit('pick', node)"
        >{{ isAiTarget ? '已选' : 'AI' }}</button>
        <button v-if="node.kind === 'failure'" type="button" title="评分 S/O/D" @click="emit('rate', node)">评分</button>
        <button v-if="canAddChild" type="button" :title="CHILD_LABEL[node.kind]" @click="emit('add', node)">＋</button>
        <button type="button" title="删除" @click="emit('remove', node)">×</button>
      </span>
    </div>

    <div v-if="open && children.length" class="fmea-children">
      <FmeaTreeNode
        v-for="child in children"
        :key="child.id"
        :node="child"
        :expanded="expanded"
        :show-unrated="showUnrated"
        :active-function-id="activeFunctionId"
        @toggle="emit('toggle', $event)"
        @pick="emit('pick', $event)"
        @rate="emit('rate', $event)"
        @add="emit('add', $event)"
        @remove="emit('remove', $event)"
      />
    </div>
  </div>
</template>
